/**
 * StoryEditor — the writing environment. Header, chapter sidebar, writing
 * canvas + toolbar, and a stats footer, plus Focus Mode / Preview / Publish
 * / Settings overlays.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Maximize2, Minimize2, Settings, Eye, MoreHorizontal,
  Copy, Archive, ArchiveRestore, Trash2, Download, Send, Menu, LayoutGrid,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useMyWriting } from './context/MyWritingContext';
import { useAutosave, useRelativeTime, useFocusMode } from './hooks';
import ChapterSidebar from './ChapterSidebar';
import EditorToolbar from './EditorToolbar';
import WritingCanvas from './WritingCanvas';
import WritingStats from './WritingStats';
import StorySettingsPanel from './StorySettingsPanel';
import PreviewMode from './PreviewMode';
import PublishModal from './PublishModal';
import { buildExport, downloadTextFile } from './utils/storyHelpers';

export default function StoryEditor() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useMediaQuery('(min-width: 900px)');
  const {
    getStory, updateStory, updateChapter, addChapter, deleteChapter,
    duplicateChapter, reorderChapters, duplicateStory, archiveStory,
    restoreStory, deleteStory, publishStory,
  } = useMyWriting();

  const story = getStory(storyId);
  const [activeChapterId, setActiveChapterId] = useState(
    searchParams.get('chapter') ?? story?.chapters[0]?.id ?? null
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isChaptersOpen, setIsChaptersOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(searchParams.get('preview') === '1');
  const [isPublishOpen, setIsPublishOpen] = useState(searchParams.get('publish') === '1');
  const { isFocusMode, toggle: toggleFocusMode, exit: exitFocusMode } = useFocusMode();
  const canvasRef = useRef(null);

  useEffect(() => {
    if (searchParams.has('preview') || searchParams.has('publish') || searchParams.has('chapter')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (story && !story.chapters.find((c) => c.id === activeChapterId)) {
      setActiveChapterId(story.chapters[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id]);

  const activeChapter = useMemo(
    () => story?.chapters.find((c) => c.id === activeChapterId) ?? null,
    [story, activeChapterId]
  );

  const { status: saveStatus, savedAt, schedule: scheduleSave } = useAutosave((content) => {
    if (story && activeChapter) updateChapter(story.id, activeChapter.id, { content });
  });
  const relativeSavedAt = useRelativeTime(savedAt);

  // Keyboard shortcuts: Cmd/Ctrl+Enter -> preview
  useEffect(() => {
    const onKeyDown = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'Enter') {
        e.preventDefault();
        setIsPreviewOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!story) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 text-center">
        <p className="font-display text-xl font-semibold text-ink">Manuscript not found.</p>
        <button type="button" onClick={() => navigate('/app/studio')} className="text-sm font-medium text-coral hover:underline">
          ← Back to Studio
        </button>
      </div>
    );
  }

  const handleCanvasInput = ({ type, value }) => {
    if (type === 'title') {
      updateChapter(story.id, activeChapter.id, { title: value });
    } else {
      scheduleSave(value);
    }
  };

  const handleExport = (format) => {
    const { content, ext, mime } = buildExport(story, format);
    downloadTextFile(`${story.title.replace(/[^\w\-]+/g, '_') || 'story'}.${ext}`, content, mime);
    toast(`Exported “${story.title}” as ${ext.toUpperCase()}`);
  };

  const handleDelete = async () => {
    if (window.confirm(`Delete “${story.title}”? This can't be undone.`)) {
      try {
        await deleteStory(story.id);
        navigate('/app/studio');
      } catch (error) {
        toast(error.message || 'We could not delete this manuscript.');
      }
    }
  };

  const saveLabel = saveStatus === 'pending' || saveStatus === 'saving'
    ? 'Saving…'
    : savedAt ? `Saved ${relativeSavedAt}` : 'Saved';

  const chapterSidebarProps = {
    story,
    activeChapterId,
    onSelectChapter: (id) => { setActiveChapterId(id); setIsChaptersOpen(false); },
    onAddChapter: async () => {
      try {
        const ch = await addChapter(story.id);
        if (ch) setActiveChapterId(ch.id);
      } catch (error) {
        toast(error.message || 'We could not create the chapter.');
      }
    },
    onRenameChapter: (chapterId, title) => updateChapter(story.id, chapterId, { title }),
    onDuplicateChapter: (chapterId) => duplicateChapter(story.id, chapterId),
    onDeleteChapter: async (chapterId) => {
      if (story.chapters.length <= 1) return;
      if (activeChapterId === chapterId) {
        const idx = story.chapters.findIndex((c) => c.id === chapterId);
        const next = story.chapters[idx - 1] ?? story.chapters[idx + 1];
        setActiveChapterId(next?.id ?? null);
      }
      try {
        await deleteChapter(story.id, chapterId);
      } catch (error) {
        toast(error.message || 'We could not delete the chapter.');
      }
    },
    onReorderChapters: (from, to) => reorderChapters(story.id, from, to),
  };

  return (
    <div className={cx('flex min-h-screen flex-col bg-cream', isFocusMode && 'bg-card')} data-testid="my-writing-editor">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-line bg-card px-4 py-3 sm:px-6">
        {!isFocusMode && (
          <button
            type="button"
            onClick={() => navigate('/app/studio')}
            data-testid="my-writing-back"
            className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-subtle hover:text-coral"
          >
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Back to Studio</span>
          </button>
        )}

        {!isDesktop && !isFocusMode && (
          <button type="button" onClick={() => setIsChaptersOpen(true)} aria-label="Chapters" className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-subtle hover:bg-cream">
            <Menu className="h-4 w-4" />
          </button>
        )}

        <input
          value={story.title}
          onChange={(e) => updateStory(story.id, { title: e.target.value })}
          data-testid="my-writing-story-title"
          className="min-w-0 flex-1 truncate bg-transparent font-display text-lg font-semibold text-ink outline-none sm:text-xl"
        />

        <span className="hidden shrink-0 text-xs text-subtle sm:inline">{saveLabel}</span>

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={isFocusMode ? exitFocusMode : toggleFocusMode}
            aria-label={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
            data-testid="my-writing-focus-toggle"
            className="hidden h-8 w-8 place-items-center rounded-full text-subtle transition-colors hover:bg-cream hover:text-ink sm:grid"
          >
            {isFocusMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>

          {!isFocusMode && (
            <>
              <button
                type="button"
                onClick={() => setIsSettingsOpen(true)}
                aria-label="Story settings"
                data-testid="my-writing-settings-toggle"
                className="hidden h-8 w-8 place-items-center rounded-full text-subtle transition-colors hover:bg-cream hover:text-ink sm:grid"
              >
                <Settings className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() => navigate(`/app/studio/${story.id}/board`)}
                data-testid="my-writing-board-open"
                className="hidden items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-coral/40 hover:text-coral sm:flex"
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Story Board
              </button>

              <button
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                data-testid="my-writing-preview-open"
                className="hidden items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-coral/40 hover:text-coral sm:flex"
              >
                <Eye className="h-3.5 w-3.5" /> Preview
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" aria-label="More" className="grid h-8 w-8 place-items-center rounded-full text-subtle hover:bg-cream hover:text-ink">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl border-line bg-card text-ink">
                  <DropdownMenuItem onClick={() => navigate(`/app/studio/${story.id}/board`)} className="sm:hidden">
                    <LayoutGrid className="mr-2 h-3.5 w-3.5" /> Story Board
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsPreviewOpen(true)} className="sm:hidden">
                    <Eye className="mr-2 h-3.5 w-3.5" /> Preview
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsSettingsOpen(true)} className="sm:hidden">
                    <Settings className="mr-2 h-3.5 w-3.5" /> Story settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleFocusMode} className="sm:hidden">
                    <Maximize2 className="mr-2 h-3.5 w-3.5" /> Focus mode
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="sm:hidden" />
                  <DropdownMenuItem onClick={() => { const s = duplicateStory(story.id); if (s) navigate(`/app/studio/${s.id}`); }}>
                    <Copy className="mr-2 h-3.5 w-3.5" /> Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('txt')}>
                    <Download className="mr-2 h-3.5 w-3.5" /> Export as TXT
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('markdown')}>
                    <Download className="mr-2 h-3.5 w-3.5" /> Export as Markdown
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {story.status === 'archived' ? (
                    <DropdownMenuItem onClick={() => restoreStory(story.id)}>
                      <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Restore
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => archiveStory(story.id)}>
                      <Archive className="mr-2 h-3.5 w-3.5" /> Move to archive
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleDelete} className="text-coral focus:text-coral">
                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {story.status !== 'published' && (
                <Button variant="primary" icon={false} onClick={() => setIsPublishOpen(true)} data-testid="my-writing-publish-open" className="ml-1 px-3 py-2 text-xs sm:px-4">
                  <Send className="h-3.5 w-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Publish</span>
                </Button>
              )}
            </>
          )}
        </div>
      </header>

      {/* Body */}
      <div className="flex min-h-0 flex-1">
        {isDesktop && !isFocusMode && (
          <aside className="w-64 shrink-0 border-r border-line bg-card px-3 py-5">
            <ChapterSidebar {...chapterSidebarProps} />
          </aside>
        )}

        <main className="flex min-w-0 flex-1 flex-col">
          {isFocusMode && (
            <div className="flex justify-center py-3">
              <button
                type="button"
                onClick={exitFocusMode}
                className="rounded-full border border-line bg-card px-3.5 py-1.5 text-xs font-semibold text-subtle shadow-soft hover:text-coral"
              >
                Exit Focus Mode (Esc)
              </button>
            </div>
          )}

          <div className={cx('sticky top-0 z-10 flex justify-center bg-cream/90 py-3 backdrop-blur', isFocusMode && 'bg-card/90')}>
            <EditorToolbar targetRef={canvasRef} onAfterCommand={scheduleSave} />
          </div>

          <WritingCanvas ref={canvasRef} chapter={activeChapter} onInput={handleCanvasInput} focusMode={isFocusMode} />

          <WritingStats story={story} chapter={activeChapter} />
        </main>
      </div>

      {/* Mobile chapter drawer */}
      <Drawer open={isChaptersOpen} onOpenChange={setIsChaptersOpen}>
        <DrawerContent className="border-line bg-card p-4 pb-8 text-ink">
          <DrawerTitle className="sr-only">Chapters</DrawerTitle>
          <ChapterSidebar {...chapterSidebarProps} />
        </DrawerContent>
      </Drawer>

      <StorySettingsPanel
        story={story}
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onUpdate={(updates) => updateStory(story.id, updates)}
      />

      {isPreviewOpen && (
        <PreviewMode
          story={story}
          onClose={() => setIsPreviewOpen(false)}
          onPublish={story.status !== 'published' ? () => { setIsPreviewOpen(false); setIsPublishOpen(true); } : undefined}
        />
      )}

      <PublishModal
        story={story}
        isOpen={isPublishOpen}
        onClose={() => setIsPublishOpen(false)}
        onPublish={(visibility) => { publishStory(story.id, visibility); toast('Story published 🐝'); }}
      />
    </div>
  );
}
